import { useState, useCallback } from 'react'
import type { CSSProperties } from 'react'
import { TopBar } from '../components/ui/TopBar'
import { ScreenWrapper } from '../components/ui/ScreenWrapper'
import { PTTButton } from '../components/radio/PTTButton'
import { PeerList } from '../components/radio/PeerList'
import { COLORS, FONT, SPACING, RADIUS } from '../components/ui/tokens'
import { useRadio } from '../hooks/useRadio'
import type { RadioChannel } from '../types/radio'

const CHANNEL_TYPE_LABEL: Record<string, string> = { public: 'Общий', local: 'Локальный · 15 км', group: 'Группа' }
const CHANNEL_TYPE_ICON: Record<string, string> = { public: '📡', local: '📍', group: '👥' }

export function RadioScreen() {
  const { channels, state, join, leave, startTransmit, stopTransmit, createChannel, getAnalyser } = useRadio()
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)

  const handleJoin = useCallback(async (ch: RadioChannel) => {
    if (state.activeChannel?.id === ch.id) { leave(); return }
    await join(ch)
  }, [state.activeChannel, join, leave])

  const handleCreate = useCallback(async () => {
    if (!newName.trim() || creating) return
    setCreating(true)
    try { await createChannel(newName.trim()); setNewName('') } finally { setCreating(false) }
  }, [newName, creating, createChannel])

  const active = state.activeChannel
  const analyser = active ? getAnalyser() : null
  const peerCount = state.peers.length

  return (
    <>
      <TopBar title="Рация" subtitle={active ? `${active.name} · ${peerCount} в эфире` : 'Выберите канал'} />
      <ScreenWrapper>
        <div style={{ padding: SPACING.md }}>
          {active ? (
            <ActivePanel channel={active} state={state} analyser={analyser} onTransmitStart={startTransmit} onTransmitStop={stopTransmit} onLeave={leave} />
          ) : (
            <div style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: SPACING.lg, textAlign: 'center', marginBottom: SPACING.md }}>
              <div style={{ fontSize: 48, marginBottom: SPACING.sm }}>📻</div>
              <div style={{ color: COLORS.textSecond, fontSize: FONT.sm }}>Выберите канал ниже для подключения</div>
            </div>
          )}

          <div style={{ marginBottom: SPACING.md }}>
            <p style={{ fontSize: FONT.xs, color: COLORS.textSecond, fontWeight: 600, letterSpacing: 0.5, marginBottom: SPACING.xs }}>КАНАЛЫ</p>
            {channels.map((ch) => <ChannelRow key={ch.id} channel={ch} isActive={active?.id === ch.id} onSelect={() => void handleJoin(ch)} />)}
          </div>

          <div>
            <p style={{ fontSize: FONT.xs, color: COLORS.textSecond, fontWeight: 600, letterSpacing: 0.5, marginBottom: SPACING.xs }}>СОЗДАТЬ ГРУППУ</p>
            <div style={{ display: 'flex', gap: SPACING.xs }}>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate() }}
                placeholder="Название канала" style={{
                  flex: 1, padding: '11px 13px', backgroundColor: COLORS.bgElevated, border: `1px solid ${COLORS.border}`,
                  borderRadius: RADIUS.md, color: COLORS.textPrimary, fontSize: FONT.sm, outline: 'none',
                }} />
              <button onClick={() => void handleCreate()} disabled={creating || !newName.trim()} style={{
                padding: '11px 18px', backgroundColor: creating || !newName.trim() ? COLORS.bgElevated : COLORS.accent,
                color: '#fff', border: 'none', borderRadius: RADIUS.md, fontSize: FONT.md, fontWeight: 700, cursor: 'pointer', opacity: creating ? 0.6 : 1,
              }}>+</button>
            </div>
          </div>

          <p style={{ fontSize: FONT.xs, color: COLORS.textDisabled, textAlign: 'center', marginTop: SPACING.md }}>Доступ к микрофону требуется только в Safari</p>
        </div>
      </ScreenWrapper>
    </>
  )
}

interface ActivePanelProps {
  channel: RadioChannel
  state: ReturnType<typeof useRadio>['state']
  analyser: AnalyserNode | null
  onTransmitStart: () => void
  onTransmitStop: () => void
  onLeave: () => void
}

function ActivePanel({ channel, state, analyser, onTransmitStart, onTransmitStop, onLeave }: ActivePanelProps) {
  const ready = !state.initializing && !state.error
  return (
    <div style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, marginBottom: SPACING.md, overflow: 'hidden' }}>
      <div style={{ padding: `${SPACING.sm}px ${SPACING.md}px`, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: FONT.base, fontWeight: 700, color: COLORS.textPrimary }}>{channel.name}</span>
          <span style={{ fontSize: FONT.xs, color: COLORS.textSecond, marginLeft: 8 }}>{CHANNEL_TYPE_LABEL[channel.type] ?? channel.type}</span>
        </div>
        <button onClick={onLeave} style={{ padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.error, border: `1px solid ${COLORS.error}`, borderRadius: RADIUS.full, fontSize: FONT.xs, cursor: 'pointer' }}>Выйти</button>
      </div>

      {state.initializing && <div style={{ padding: SPACING.md, textAlign: 'center', color: COLORS.textSecond, fontSize: FONT.sm }}>Подключение микрофона...</div>}
      {state.error && (
        <div style={{ padding: SPACING.md, margin: SPACING.md, backgroundColor: COLORS.error + '18', border: `1px solid ${COLORS.error}`, borderRadius: RADIUS.md, color: COLORS.error, fontSize: FONT.sm }}>
          ⚠️ {state.error}
        </div>
      )}

      {ready && (
        <div style={{ padding: SPACING.lg, textAlign: 'center' }}>
          <PTTButton transmitting={state.transmitting} analyser={analyser} disabled={false} onStart={onTransmitStart} onStop={onTransmitStop} />
          <div style={{ fontSize: FONT.xs, color: COLORS.textDisabled, marginTop: SPACING.sm }}>{state.transmitting ? '🔴 В эфире...' : 'Удерживайте кнопку для разговора'}</div>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: `${SPACING.xs}px 0` }}>
        <p style={{ fontSize: FONT.xs, color: COLORS.textSecond, fontWeight: 600, letterSpacing: 0.5, padding: `${SPACING.xs}px ${SPACING.md}px` }}>УЧАСТНИКИ</p>
        <PeerList peers={state.peers} />
      </div>
    </div>
  )
}

interface ChannelRowProps { channel: RadioChannel; isActive: boolean; onSelect: () => void }

function ChannelRow({ channel, isActive, onSelect }: ChannelRowProps) {
  const style: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: SPACING.sm, padding: `${SPACING.sm}px ${SPACING.md}px`,
    backgroundColor: isActive ? COLORS.accent + '18' : COLORS.bgCard, border: `1px solid ${isActive ? COLORS.accent : COLORS.border}`,
    borderRadius: RADIUS.md, marginBottom: SPACING.xs, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  }
  return (
    <div style={style} onClick={onSelect}>
      <span style={{ fontSize: 22 }}>{CHANNEL_TYPE_ICON[channel.type] ?? '📻'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: FONT.base, color: COLORS.textPrimary, fontWeight: isActive ? 600 : 400 }}>{channel.name}</div>
        <div style={{ fontSize: FONT.xs, color: COLORS.textSecond }}>{CHANNEL_TYPE_LABEL[channel.type] ?? channel.type}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
        <span style={{ fontSize: FONT.xs, color: COLORS.textSecond }}>{channel.memberCount}</span>
        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isActive ? COLORS.success : COLORS.textDisabled }} />
      </div>
    </div>
  )
}
